import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { ActNatures } from "../../classes/enums/SurgicalActsEnum";
import { GET_SURGICAL_ACTS_BY_IDS } from "../../queries/surgicalActsQueries";

export function useSurgicalActs(surgicalActIds = []) {
  const [formattedSurgicalActs, setFormattedSurgicalActs] = useState([]);
  const [formattedGlobalAlignmentAct, setFormattedGlobalAlignmentAct] =
    useState([]);

  const { data, loading, error, refetch } = useQuery(GET_SURGICAL_ACTS_BY_IDS, {
    variables: { ids: surgicalActIds },
    skip: !surgicalActIds || surgicalActIds.length === 0,
  });

  useEffect(() => {
    if (data?.surgicalActsByIds) {
      const rawActs = data.surgicalActsByIds;
      const regularActs = [];
      const globalAlignmentAct = [];

      rawActs.forEach(act => {
        const { _id, actNature, vertebra, value, details } = act;
        const { __typename, ...cleanDetails } = details || {};

        // Check if the act is a global alignment act
        if (actNature === ActNatures.GlobalAlignment) {
          globalAlignmentAct.push({
            _id,
            actNature,
            vertebra,
            value,
            pelvicTilt: cleanDetails.globalAlignment?.pelvicTilt,
            kneeAngle: cleanDetails.globalAlignment?.kneeAngle,
            lumbarLordosis: cleanDetails.globalAlignment?.lumbarLordosis,
            sacralSlope: cleanDetails.globalAlignment?.sacralSlope,
            globalTilt: cleanDetails.globalAlignment?.globalTilt,
            method: cleanDetails.globalAlignment?.method,
            targetPelvicTilt: cleanDetails.globalAlignment?.targetPelvicTilt,
            t1pa: cleanDetails.globalAlignment?.t1pa,
            piLl: cleanDetails.globalAlignment?.piLl,
            originalPelvicTilt:
              cleanDetails.globalAlignment?.originalPelvicTilt,
            originalKneeAngle: cleanDetails.globalAlignment?.originalKneeAngle,
            originalLumbarLordosis:
              cleanDetails.globalAlignment?.originalLumbarLordosis,
            originalSacralSlope:
              cleanDetails.globalAlignment?.originalSacralSlope,
            originalGlobalTilt:
              cleanDetails.globalAlignment?.originalGlobalTilt,
            originalTargetPelvicTilt:
              cleanDetails.globalAlignment?.originalTargetPelvicTilt,
            originalT1pa: cleanDetails.globalAlignment?.originalT1pa,
            originalPiLl: cleanDetails.globalAlignment?.originalPiLl,
          });
          return;
        }

        let formattedAct;
        switch (actNature) {
          case ActNatures.Osteotomy:
            formattedAct = {
              _id,
              actNature,
              vertebra,
              value,
              osteotomyType: cleanDetails.osteotomyType,
              supportPoints: cleanDetails.supportPoints,
            };
            break;
          case ActNatures.SegmentalTransformation:
            formattedAct = {
              _id,
              actNature,
              vertebra,
              value,
              rotation: cleanDetails.rotation,
              // Assuming the db stores these under supportPointsSegmental
              supportPoints: cleanDetails.supportPointsSegmental,
              type: cleanDetails.type,
              xShift: cleanDetails.xShift,
              yShift: cleanDetails.yShift,
            };
            break;
          case ActNatures.Cage:
            formattedAct = {
              _id,
              actNature,
              vertebra,
              value,
              cageHeight: cleanDetails.cageHeight,
              cagePosition: cleanDetails.cagePosition,
              cageWidth: cleanDetails.cageWidth,
              maxWidth: cleanDetails.maxWidth,
              minHeight: cleanDetails.minHeight,
              maxHeight: cleanDetails.maxHeight,
              // For cages, we rename rotation to rotationCage in the db
              rotation: cleanDetails.rotationCage,
              supportPoints: cleanDetails.supportPointsCage,
              type: cleanDetails.type,
              vertebraBelow: cleanDetails.vertebraBelow,
            };
            break;
          case ActNatures.Screw:
            formattedAct = {
              _id,
              actNature,
              vertebra,
              value,
              screwDiameter: cleanDetails.screwDiameter,
              screwLength: cleanDetails.screwLength,
              supportPoints: cleanDetails.supportPointsScrew,
            };
            break;
          case ActNatures.Rod:
            formattedAct = {
              _id,
              actNature,
              vertebra,
              value,
              // Original rod fields
              rodPairId: cleanDetails.rodPairId,
              rodVertebraeSubset: cleanDetails.rodVertebraeSubset,
              rodDiameter: cleanDetails.rodDiameter,
              side: cleanDetails.side,
              // Rod specifications
              rodType: cleanDetails.rodType,
              rodManufacturer: cleanDetails.rodManufacturer,
              rodProduct: cleanDetails.rodProduct,
              rodExtraLength: cleanDetails.rodExtraLength,
              rodMaterial: cleanDetails.rodMaterial,
              rodCalculatedLength: cleanDetails.rodCalculatedLength,
              // Screw specifications
              screwType: cleanDetails.screwType,
              screwManufacturer: cleanDetails.screwManufacturer,
              screwProduct: cleanDetails.screwProduct,
              screwDiameter: cleanDetails.screwDiameter,
              screwLength: cleanDetails.screwLength,
              screwMaterial: cleanDetails.screwMaterial,
              screwCount: cleanDetails.screwCount,
              screws: cleanDetails.screws,
            };
            break;
          case ActNatures.ReciprocalChange:
            formattedAct = {
              _id,
              actNature,
              vertebra,
              value,
              spineRigidityType:
                cleanDetails.reciprocalChange?.spineRigidityType,
              warning: cleanDetails.reciprocalChange?.warning,
            };
            break;
          default:
            // In case a new type is added in the future
            formattedAct = { _id, actNature, vertebra, value, ...cleanDetails };
        }
        regularActs.push(formattedAct);
      });

      setFormattedSurgicalActs(regularActs);
      setFormattedGlobalAlignmentAct(globalAlignmentAct);
    }
  }, [data]);

  return {
    surgicalActs: formattedSurgicalActs,
    globalAlignmentAct: formattedGlobalAlignmentAct,
    loading,
    error,
    refetch,
  };
}
